<template>
  <div v-if="!roleReady" class="dashboard-role-state" :role="roleError ? 'alert' : 'status'" aria-live="polite">
    <template v-if="roleError"><p>{{ roleError }}</p><button type="button" @click="refreshIdentity">{{ $LANG('', '重新读取用户角色') }}</button></template>
    <template v-else><i class="el-icon-loading" aria-hidden="true" /><span>{{ $LANG('', '正在读取用户角色') }}</span></template>
  </div>
  <SupplierWorkbench v-else-if="isSupplier" :key="sessionKey" :api="supplierApi" :context="context" />
  <LegacyMain v-else :key="sessionKey" />
</template>
<script>
export const SUPPLIER_ROLE_CODE = '006'
export const SUPPLIER_IDENTITY_EVENT = 'supplier-identity-changed'

function readCookie(cookies, key) {
  try {
    return cookies ? cookies.get(key) : null
  } catch (_) {
    return null
  }
}

function hasCookie(cookies, key) {
  try {
    return Boolean(cookies && typeof cookies.isKey === 'function' && cookies.isKey(key))
  } catch (_) {
    return false
  }
}

function readStorage(storage, key) {
  try {
    return storage ? storage.getItem(key) : null
  } catch (_) {
    return null
  }
}

function sessionSubject(cookies) {
  const username = readCookie(cookies, 'v_telno')
  const corpId = readCookie(cookies, 'v_corpid')
  return username && corpId ? JSON.stringify([String(username), String(corpId)]) : ''
}

function normalizeRole(value) {
  return typeof value === 'string' ? value.trim() : ''
}

export function readSessionRole({ cookies, storage } = {}) {
  const unverified = { verified: false, roleId: '' }
  const subject = sessionSubject(cookies)
  if (!subject) return unverified

  const cookieRole = readCookie(cookies, 'v_roleid')
  // An explicitly cleared cookie also wins, so old storage cannot revive a role.
  const useCookie = (cookieRole !== null && cookieRole !== undefined) || hasCookie(cookies, 'v_roleid')
  const roleId = normalizeRole(useCookie ? cookieRole : readStorage(storage, 'v_roleid'))
  const boundSubject = useCookie
    ? readCookie(cookies, 'v_supplier_subject')
    : readStorage(storage, 'v_supplier_subject')

  return roleId && boundSubject === subject ? { verified: true, roleId } : unverified
}

function writeIdentity({ cookies, storage }, roleId) {
  const flag = roleId === SUPPLIER_ROLE_CODE ? '1' : '0'
  const subject = sessionSubject(cookies)
  try {
    if (cookies) {
      cookies.set('v_roleid', roleId)
      cookies.set('v_supplier_subject', subject)
      cookies.set('v_is_supplier', flag)
    }
  } catch (_) {
    // Cookie restrictions must not interrupt a completed authentication request.
  }
  try {
    if (storage) {
      storage.setItem('v_roleid', roleId)
      storage.setItem('v_supplier_subject', subject)
      storage.setItem('v_is_supplier', flag)
    }
  } catch (_) {
    // The matching cookie role remains usable when storage is unavailable.
  }
  try {
    if (storage) storage.removeItem('mock_is_supplier')
  } catch (_) {
    // Legacy test preferences never participate in role resolution.
  }
  return roleId === SUPPLIER_ROLE_CODE
}

export function persistSupplierIdentity({ cookies, storage, roleId } = {}) {
  const supplier = writeIdentity({ cookies, storage }, normalizeRole(roleId))
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new window.Event(SUPPLIER_IDENTITY_EVENT))
    }
  } catch (_) {
    // A missing browser event API must not interrupt successful login.
  }
  return supplier
}

export function clearSupplierIdentity({ cookies, storage } = {}) {
  return writeIdentity({ cookies, storage }, '')
}

export async function fetchSessionRole({ axios, username } = {}) {
  if (typeof username !== 'string' || !username.trim()) {
    throw new Error('缺少登录账号，无法获取登录角色，请重新登录')
  }
  let response
  try {
    response = await axios({
      method: 'post',
      url: 'xuper_auth/getBaseData',
      data: { p_table: 'v_sys_user', p_key: 'userno', p_value: username }
    })
  } catch (_) {
    throw new Error('获取登录角色失败，请重试')
  }
  if (!response || String(response.code) !== '200') {
    const detail = response && typeof response.result === 'string' ? response.result.trim() : ''
    throw new Error(detail ? `获取登录角色失败：${detail}` : '获取登录角色失败，请重试')
  }
  const row = Array.isArray(response.result) ? response.result[0] : null
  const roleId = row && normalizeRole(row.ROLENO)
  if (!roleId) throw new Error('未查询到有效的登录角色，请重试或联系管理员')
  return roleId
}


export default {
  name: 'Mainpage',
  components: {
    SupplierWorkbench: () => import('./index.vue'),
    LegacyMain: () => import('../../views/Main.vue')
  },
  props: { supplierApi: { type: Object, default: null }, context: { type: Object, default: null } },
  data() { return { isSupplier: false, roleReady: false, roleLoading: false, roleError: '', roleEpoch: 0, roleSubject: '', sessionKey: '', listening: false } },
  created() { this.refreshIdentity() },
  mounted() { this.listen() },
  activated() { this.refreshIdentity(); this.listen() },
  deactivated() { this.cancelRoleLookup(); this.unlisten() },
  beforeDestroy() { this.cancelRoleLookup(); this.unlisten() },
  methods: {
    currentSession() {
      return { user: this.$cookies && this.$cookies.get('v_telno') || '', corp: this.$cookies && this.$cookies.get('v_corpid') || '' }
    },
    applyRole(roleId, user, corp) {
      this.isSupplier = roleId === SUPPLIER_ROLE_CODE
      this.sessionKey = JSON.stringify([user, corp, roleId])
      this.roleError = ''; this.roleReady = true
    },
    async refreshIdentity() {
      let storage = null
      try { storage = window.localStorage } catch (_) { /* Cookie-backed roles remain usable when storage is blocked. */ }
      const { user, corp } = this.currentSession()
      const subject = JSON.stringify([user, corp])
      const cached = readSessionRole({ cookies: this.$cookies, storage })
      if (cached.verified) {
        this.cancelRoleLookup()
        this.applyRole(cached.roleId, user, corp)
        return
      }
      if (this.roleLoading && this.roleSubject === subject) return this._rolePromise.catch(() => undefined)
      const epoch = ++this.roleEpoch
      this.roleReady = false; this.roleError = ''; this.roleSubject = subject
      if (!user || !corp) {
        this.roleLoading = false
        this.roleError = this.$LANG('', '会话信息不完整，请重新登录')
        return
      }
      this.roleLoading = true
      this._rolePromise = fetchSessionRole({ axios: this.$axios, username: user })
      try {
        const roleId = await this._rolePromise
        if (epoch !== this.roleEpoch) return
        const current = this.currentSession()
        if (String(current.user) !== String(user) || String(current.corp) !== String(corp)) {
          this.roleLoading = false
          return this.refreshIdentity()
        }
        persistSupplierIdentity({ cookies: this.$cookies, storage, roleId })
        this.applyRole(roleId, user, corp)
      } catch (error) {
        if (epoch === this.roleEpoch) this.roleError = this.$LANG('', '读取用户角色失败，请重试') + '：' + error.message
      } finally {
        if (epoch === this.roleEpoch) { this.roleLoading = false; this.roleSubject = '' }
      }
    },
    cancelRoleLookup() { this.roleEpoch++; this.roleLoading = false; this.roleSubject = '' },
    listen() {
      if (this.listening) return
      window.addEventListener('storage', this.refreshIdentity)
      window.addEventListener('focus', this.refreshIdentity)
      window.addEventListener(SUPPLIER_IDENTITY_EVENT, this.refreshIdentity)
      this.listening = true
    },
    unlisten() {
      window.removeEventListener('storage', this.refreshIdentity)
      window.removeEventListener('focus', this.refreshIdentity)
      window.removeEventListener(SUPPLIER_IDENTITY_EVENT, this.refreshIdentity)
      this.listening = false
    }
  }
}
</script>
<style scoped>
.dashboard-role-state { min-height: 240px; display: flex; align-items: center; justify-content: center; gap: 10px; background: #f8fafc; color: #64748b; font-size: 13px; }
.dashboard-role-state[role="alert"] { flex-direction: column; }
.dashboard-role-state p { margin: 0; }
.dashboard-role-state button { padding: 7px 12px; background: #fff; color: #2563eb; border: 1px solid #dbeafe; border-radius: 6px; cursor: pointer; }
</style>
